/**
 * Enterprise Notification Service
 * Provides helper methods to trigger notifications from anywhere in the app.
 * This is for in-app toast notifications (UI popups), NOT the bell-icon notifications.
 */

import type {
 Notification,
 NotificationOptions,
 NotificationType,
} from "./notification.types";
import { DURATION_BY_TYPE } from "./notification.types";

type NotificationListener = (notification: Notification) => void;
type DismissListener = (id: string) => void;

let idCounter = 0;
const listeners: Set<NotificationListener> = new Set();
const dismissListeners: Set<DismissListener> = new Set();

function generateId(): string {
 idCounter += 1;
 return`notif-${Date.now()}-${idCounter}`;
}

function createNotification(options: NotificationOptions): Notification {
 const type = options.type ??"info";
 return {
 id: generateId(),
 type,
 title: options.title,
 message: options.message,
 timestamp: Date.now(),
 isRead: false,
 isDismissed: false,
 priority: options.priority ??"normal",
 actionUrl: options.actionUrl,
 actionLabel: options.actionLabel,
 duration: options.duration ?? DURATION_BY_TYPE[type],
 phase: "entering",
 };
}

function emit(notification: Notification) {
 listeners.forEach((fn) => fn(notification));
}

/* ─── Public API ─── */

export function subscribe(fn: NotificationListener): () => void {
 listeners.add(fn);
 return () => {
 listeners.delete(fn);
 };
}

export function onDismiss(fn: DismissListener): () => void {
 dismissListeners.add(fn);
 return () => {
 dismissListeners.delete(fn);
 };
}

export function dismissNotification(id: string) {
 dismissListeners.forEach((fn) => fn(id));
}

/* ─── Helper Methods ─── */

function notify(type: NotificationType, title: string, message?: string, options?: Partial<NotificationOptions>) {
 const notification = createNotification({
 type,
 title,
 message,
 ...options,
 });
 emit(notification);
 return notification.id;
}

export function notifySuccess(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("success", title, message, options);
}

export function notifyInfo(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("info", title, message, options);
}

export function notifyWarning(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("warning", title, message, options);
}

export function notifyError(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("error", title, message, { duration: 8000, ...options });
}

export function notifySystem(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("system", title, message, options);
}

export function notifySecurity(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("security", title, message, { duration: 10000, ...options });
}

export function notifyTask(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("task", title, message, options);
}

export function notifyProject(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("project", title, message, options);
}

export function notifyLeave(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("leave", title, message, options);
}

export function notifyTimeTracking(title: string, message?: string, options?: Partial<NotificationOptions>) {
 return notify("time", title, message, options);
}

/** Notify from API errors automatically */
export function notifyApiError(error: unknown, fallbackTitle?: string) {
 const axiosError = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
 const status = axiosError?.response?.status;
 const serverMessage = axiosError?.response?.data?.message;
 const message = serverMessage || axiosError?.message ||"An unexpected error occurred";

 let title = fallbackTitle ||"Request Failed";

 if (status === 404) title ="Not Found";
 else if (status === 403) title ="Access Denied";
 else if (status === 422) title ="Validation Error";
 else if (status === 429) title ="Rate Limited";
 else if (status === 500) title ="Server Error";
 else if (status === 503) title ="Service Unavailable";
 else if (!status) title ="Network Error";

 return notifyError(title, message, {
 priority: status && status >= 500 ?"high":"normal",
 });
}
